import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { EMPTY, from, Observable, Subject, switchMap, take, throwError } from 'rxjs';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { catchError, filter, map } from 'rxjs/operators';
import { CryptoDecryptService } from '../services/crypto-decrypt.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new Subject<any>();

  constructor(
    private cryptoDecryptService: CryptoDecryptService,
    private jwtAuth: JwtAuthService) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.jwtAuth.token || this.jwtAuth.getJwtToken();
    return next.handle(this.addTokenHeader(req)).pipe(
      switchMap((event: HttpResponse<any>) => {
        return this.fixBodyResponse(event, token);
      }),
      catchError((error: HttpErrorResponse) => {
        if (req.url.includes('token')) {
          if (this.refreshTokenInProgress) {
            this.jwtAuth.logout(true);
            return EMPTY;
          }
          return this.formatError(error);
        }

        if (error && error.status === 401) {
          if (this.refreshTokenInProgress) {
            // Se já estiver em refresh, espera até concluir
            return this.refreshTokenSubject.pipe(
              filter(result => result !== null),
              take(1),
              switchMap(() => {
                this.refreshTokenSubject.next(true);
                return next.handle(this.addTokenHeader(req)).pipe(
                  switchMap((event: HttpResponse<any>) => {
                    return this.fixBodyResponse(event, token);
                  }),
                );
              }),
            );
          } else {
            if (this.jwtAuth.getJwtRefreshToken()) {
              this.refreshTokenInProgress = true;
              return this.jwtAuth.refreshToken().pipe(
                switchMap(() => {
                  this.refreshTokenSubject.next(true);
                  return next.handle(this.addTokenHeader(req)).pipe(
                    switchMap((event: HttpResponse<any>) => {
                      return this.fixBodyResponse(event, token);
                    }),
                  );
                }),
              );
            } else {
              this.jwtAuth.logout(true);
            }
          }
        }
        return this.formatError(error);
      }),
    );
  }

  fixBodyResponse(event: HttpResponse<any>, token) {
    if (
      event instanceof HttpResponse
      && ['rbe1c2', 'rbe1c1'].includes(event.headers.get('content-encoding'))
    ) {
      const encoding: any = event.headers.get('content-encoding');
      return from(
        this.cryptoDecryptService.decryptBody(event.body, token, encoding)
      ).pipe(
        map(decryptedBody => {
          return event.clone({
            body: decryptedBody,
          });
        })
      );
    } else if (event instanceof HttpResponse) {
      let parsedBody: any;
      try {
        if (event.body instanceof ArrayBuffer) {
          // Se for ArrayBuffer → converte para string e tenta JSON
          const text = new TextDecoder().decode(event.body);
          parsedBody = JSON.parse(text);
        } else if (typeof event.body === 'string') {
          // Se for string → tenta JSON.parse
          parsedBody = JSON.parse(event.body);
        } else {
          // Se já for objeto/json ou outro tipo → usa direto
          parsedBody = event.body;
        }
      } catch (e) {
        console.warn('⚠️ Falha ao parsear body, retornando cru:', e);
        parsedBody = event.body;
      }

      return [event.clone({ body: parsedBody })];
    }

    return [event];
  }

  formatError(error: HttpErrorResponse) {
    if (error?.error) {
      try {
        let parsedError: any;

        if (error.error instanceof ArrayBuffer) {
          // Decodifica ArrayBuffer -> string
          const decoded = new TextDecoder().decode(error.error);

          try {
            parsedError = JSON.parse(decoded);
          } catch {
            parsedError = decoded; // Se não for JSON, devolve como string
          }
        } else if (typeof error.error === 'string') {
          // Se já for string, tenta parsear como JSON
          try {
            parsedError = JSON.parse(error.error);
          } catch {
            parsedError = error.error; // Se não for JSON, mantém a string
          }
        } else {
          // Já é objeto ou outro tipo (ex.: JSON já pronto)
          parsedError = error.error;
        }

        // Retorna o mesmo HttpErrorResponse, apenas trocando o campo "error"
        return throwError(() => new HttpErrorResponse({ ...error, error: parsedError }));
      } catch (e) {
        console.warn('⚠️ Falha ao processar erro:', e);
        return throwError(() => error);
      }
    }
  }

  private addTokenHeader(req: HttpRequest<any>, token?: string): HttpRequest<any> {
    const tkn = token || this.jwtAuth.token || this.jwtAuth.getJwtToken();
    const includeApi = environment.apiUrl;
    const excludeToken = '/v1/token';

    const baseHeaders: Record<string, string> = {
      'X-Mar-Locale': this.jwtAuth.locale,
    };

    if (
      tkn &&
      req.url.includes(includeApi) &&
      !req.url.includes(excludeToken)
    ) {
      return req.clone({
        setHeaders: {
          ...baseHeaders,
          Authorization: `Bearer ${ tkn }`,
        },
        responseType: 'arraybuffer' as 'json',
      });
    }

    // Mesmo se não houver token, mantém o header de locale
    return req.clone({
      setHeaders: {
        ...baseHeaders,
      },
    });
  }
}
